import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useCompanies } from "../../../../hooks/companies";
import { useDateFilter } from "../../../../hooks/datefilter";
import { normalizarQuadro, obterQuadro } from "../../../../services/despachoIndicadoresApi";
import { chaveRecorte, periodoDespacho } from "./useResumoDespacho";

// Dados do Quadro do despacho na Torre e no Meu Painel (PO1-3500, épico
// PO1-3493): UMA chamada a /despacho/indicadores/quadro por recorte
// (embarcador + transportadora + período, padrão 30 dias). Não passa pelo
// /resumo: os outros 6 blocos da aba continuam com a chamada única deles.
//
// Contrato de orquestração da Torre igual ao useResumoDespacho: com
// `deferLoad`, a PRIMEIRA carga fica com a fila da página (`onRegisterFetch`);
// trocas de recorte depois disso carregam na hora. Sem props (Meu Painel)
// carrega ao montar.
//
// Só a resposta da ÚLTIMA consulta escreve no estado (versão), como na tela de
// indicadores: o axios do projeto (0.19) não aceita AbortController, então o
// useCancelableFetch dos gráficos de custo não cancelaria nada aqui.

// Torre: 10 cartões por coluna (ver QuadroDespacho).
export const LIMITE_QUADRO_TORRE = 10;

const VAZIO = normalizarQuadro(null);

export default function useQuadroDespacho({ deferLoad = false, onRegisterFetch, limite = LIMITE_QUADRO_TORRE } = {}) {
  const { empresa, transportadora, empresaId, arrayParams } = useCompanies();
  const { startDate, endDate } = useDateFilter();

  const periodo = useMemo(() => periodoDespacho(startDate, endDate), [startDate, endDate]);
  const transportadoras = arrayParams.transportadoras || null;
  const chave = `${chaveRecorte(empresaId, transportadoras, periodo)}|${limite}`;
  // Params derivados da chave (string estável), não do array do contexto.
  const params = useMemo(() => {
    const p = { ...periodo, limite };
    if (transportadoras && transportadoras.length) p.transportadoras = transportadoras;
    return p;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chave]);

  const [estado, setEstado] = useState({ dados: VAZIO, carregando: false, erro: null, carregado: false });

  const montado = useRef(true);
  useEffect(
    () => () => {
      montado.current = false;
    },
    []
  );

  const versao = useRef(0);
  const carregar = useCallback(async () => {
    versao.current += 1;
    const minha = versao.current;
    const atual = () => montado.current && minha === versao.current;
    setEstado((e) => ({ ...e, carregando: true, erro: null }));
    try {
      const dados = await obterQuadro(params);
      if (atual()) setEstado({ dados, carregando: false, erro: null, carregado: true });
      return dados;
    } catch (e) {
      if (atual()) setEstado({ dados: VAZIO, carregando: false, erro: e, carregado: true });
      return null;
    }
  }, [params]);

  // Primeira carga segue o contrato da Torre; depois disso toda troca de
  // recorte carrega na hora (a fila da página só roda ao trocar de aba).
  // `ultimaChave` evita repetir a chamada quando o efeito roda de novo no mesmo
  // recorte (o pai passa `onRegisterFetch` inline, nova função a cada render).
  const jaCarregou = useRef(false);
  const ultimaChave = useRef(null);
  useEffect(() => {
    if (onRegisterFetch) {
      onRegisterFetch(() => {
        if (jaCarregou.current && ultimaChave.current === chave) return Promise.resolve(null);
        jaCarregou.current = true;
        ultimaChave.current = chave;
        return carregar();
      });
    }
    if ((!deferLoad || jaCarregou.current) && ultimaChave.current !== chave) {
      jaCarregou.current = true;
      ultimaChave.current = chave;
      carregar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carregar, deferLoad, onRegisterFetch, empresa, transportadora]);

  return { ...estado, periodo, recarregar: carregar };
}
